import React, { useContext, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageTitle, Card, EmptyState, Button } from '../components/common';
import { AuthContext } from '../contexts/AuthContext';
import { mockCourses } from '../mockData';
import { Course } from '../types';
import { useToast } from '../components/Toaster';

const DEFAULT_COURSE_IMAGE = 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=870&q=80';

// Icons
const StarIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
    <svg className={`${className} text-yellow-400`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);
const TrashIcon = () => <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const HeartIcon: React.FC<{ isFilled: boolean }> = ({ isFilled }) => (
    <svg className={`w-6 h-6 ${isFilled ? 'text-red-500' : 'text-white'}`} fill={isFilled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={isFilled ? 0 : 2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 016.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" />
    </svg>
);

const CourseCard: React.FC<{ course: Course; onRemove: (courseId: string, courseTitle: string) => void; }> = ({ course, onRemove }) => {
    
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.src = DEFAULT_COURSE_IMAGE;
    };

    const price = course.price ?? 0;
    const discount = course.discount ?? 0;
    const finalPrice = price > 0 ? price - (price * discount / 100) : 0;

    return (
        <Card className="overflow-hidden flex flex-col group transition-all duration-300">
            <div className="relative">
                <Link to={`/courses/${course.id}`} className="block overflow-hidden h-48 rounded-t-2xl">
                    <img 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        src={course.imageUrl || DEFAULT_COURSE_IMAGE} 
                        alt={course.title}
                        onError={handleImageError}
                    />
                </Link>
                <button 
                    onClick={() => onRemove(course.id, course.title)} 
                    className="absolute top-3 right-3 bg-gray-900/30 p-2 rounded-full backdrop-blur-sm transition-all hover:bg-gray-900/50"
                    aria-label={`Remove ${course.title} from wishlist`}
                 >
                    <HeartIcon isFilled={true} />
                </button>
            </div>
            <div className="p-4 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-3">
                    <img src={course.instructorAvatar} alt={course.instructor} className="w-8 h-8 rounded-full object-cover" />
                    <span className="text-sm font-semibold text-gray-800 flex-grow">{course.instructor}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{course.category}</span>
                </div>
                <h3 className="text-md font-bold text-gray-900 leading-snug flex-grow mb-2 group-hover:text-primary transition-colors">
                    <Link to={`/courses/${course.id}`}>{course.title}</Link>
                </h3>
                <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-4">
                    <StarIcon />
                    <span className="font-bold text-gray-800">{course.rating}</span>
                    <span>({course.reviewCount} Reviews)</span>
                </div>
                <div className="flex justify-between items-center mt-auto">
                    <p className="text-xl font-bold text-red-500">${finalPrice.toFixed(2)}</p>
                    <Button as={Link} to={`/courses/${course.id}`} size="sm" variant='primary'>
                        View Course
                    </Button>
                </div>
            </div>
        </Card>
    );
};


const WishlistPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const toast = useToast();
    const [wishlistIds, setWishlistIds] = useState(auth?.currentUser?.wishlistedCourseIds || []);

    const wishlistedCourses = useMemo(() => 
        mockCourses.filter(course => wishlistIds.includes(course.id)),
        [wishlistIds]
    );
    
    const handleRemove = (courseId: string, courseTitle: string) => {
        setWishlistIds(prev => prev.filter(id => id !== courseId));
        toast.success(`"${courseTitle}" removed from wishlist.`);
    };
    
    const handleClearAll = () => {
        setWishlistIds([]);
        toast.success("Wishlist cleared!");
    };

    return (
        <div>
            <PageTitle title="My Wishlist" subtitle="Courses you're interested in.">
                {wishlistedCourses.length > 0 && (
                    <Button variant="danger" size="sm" onClick={handleClearAll}>
                        <TrashIcon />
                        Clear Wishlist
                    </Button>
                )}
            </PageTitle>
            
            {wishlistedCourses.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {wishlistedCourses.map(course => (
                        <CourseCard key={course.id} course={course} onRemove={handleRemove} />
                    ))}
                </div>
            ) : (
                <EmptyState 
                    title="Your Wishlist is Empty"
                    message="Add courses to your wishlist to keep track of what you want to learn."
                    action={<Button as={Link} to="/courses">Explore Courses</Button>}
                />
            )}
        </div>
    );
};

export default WishlistPage;